import { DEFAULT_REQUEST_TIMEOUT_MS } from '@/libs/request/constants';

/**
 * Builds the AbortSignal for a fetch: a timeout signal (default 15s) optionally
 * combined with a caller-provided cancellation signal.
 *
 * The combination is feature-detected: the server (Node 20) always supports
 * AbortSignal.timeout/any, but request() and getUserById also run in the browser,
 * and older browsers lack these. On such runtimes this degrades gracefully to the
 * previous behaviour (caller signal honoured, no timeout applied) instead of
 * throwing, so it never regresses client features.
 *
 * Pass timeoutMs = 0 to opt out of the timeout for a specific request.
 */
export default function createRequestSignal(
  callerSignal?: AbortSignal | null,
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS,
): AbortSignal | undefined {
  const canTimeout = timeoutMs > 0
    && typeof AbortSignal !== 'undefined'
    && typeof AbortSignal.timeout === 'function';
  const timeoutSignal = canTimeout ? AbortSignal.timeout(timeoutMs) : null;

  if (callerSignal && timeoutSignal) {
    return typeof AbortSignal.any === 'function'
      ? AbortSignal.any([callerSignal, timeoutSignal])
      : callerSignal;
  }

  return callerSignal ?? timeoutSignal ?? undefined;
}
